import React, { useCallback, useEffect } from "react";
import { IconButton } from "@mui/material";
import { BackIcon, CloseIcon } from "../../../components/Icons";
import { useModal } from "../../../context/ModalContext";
import { useCustomerSupportForm, SupportStep } from "../../../hooks/useCustomerSupportForm";
import LandingStep from "../../containers/CustomerSupportContainers/LandingStep/LandingStep";
import PetSelectionStep
  from "../../containers/CustomerSupportContainers/PetSelectionStep/PetSelectionStep";
import TicketFormStep
  from "../../containers/CustomerSupportContainers/TicketFormStep/TicketFormStep";
import { userActivityLogger } from "../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";

const CustomerSupportModal = () => {
  const { closeModal } = useModal();

  useEffect(() => {
    userActivityLogger(MIXPANEL_EVENTS.CUSTOMER_SUPPORT_MODAL_OPENED, {});
  }, []);

  const handleClose = useCallback(() => {
    userActivityLogger(MIXPANEL_EVENTS.CUSTOMER_SUPPORT_MODAL_CLOSED, {});
    closeModal();
  }, [closeModal]);

  const {
    currentStep,
    selectedPets,
    attachment,
    fileError,
    loading,
    control,
    canContinueFromPets,
    isFormValid,
    subjectOptions,
    goToSubmitFlow,
    goToForm,
    goBackToPetSelection,
    handleSelectPet,
    handleSelectAll,
    handleFileSelect,
    removeFile,
    handleSubmit,
  } = useCustomerSupportForm();

  const showBackButton = currentStep === SupportStep.FORM;

  const renderStep = () => {
    switch (currentStep) {
    case SupportStep.LANDING:
      return <LandingStep onSubmitTicket={goToSubmitFlow} />;
    case SupportStep.PET_SELECTION:
      return (
        <PetSelectionStep
          selectedPets={selectedPets}
          onSelectPet={handleSelectPet}
          onSelectAll={handleSelectAll}
          onContinue={goToForm}
          canContinue={canContinueFromPets}
        />
      );
    case SupportStep.FORM:
      return (
        <TicketFormStep
          control={control}
          attachment={attachment}
          fileError={fileError}
          loading={loading}
          isFormValid={isFormValid}
          subjectOptions={subjectOptions}
          onFileSelect={handleFileSelect}
          onRemoveFile={removeFile}
          onSubmit={handleSubmit}
        />
      );
    default:
      return null;
    }
  };

  return (
    <div className="flex flex-col w-full xl:w-[480px] max-h-[80vh] p-8 bg-gray-2 rounded-3xl relative">
      {showBackButton && (
        <IconButton onClick={goBackToPetSelection} size="small" className="!absolute top-8 left-8">
          <BackIcon />
        </IconButton>
      )}
      <IconButton onClick={handleClose} size="small" className="!absolute top-8 right-8">
        <CloseIcon color="#262626" />
      </IconButton>

      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        {renderStep()}
      </div>
    </div>
  );
};

export default CustomerSupportModal;
